import type { IReportsApiPort } from '@reports/application/ports';
import type { BlockDataRequestDto } from '@reports/application/dtos';
import type {
  ReportFilterConfig,
  ReportFilterOptions,
  TableBlockData,
  ChartBlockData,
  SummaryCardBlockData,
} from '@reports/domain/value-objects';
import type { ReportsHttpClientType } from '@reports/infrastructure/api/types';
import type { ReportApiMapperType } from '@reports/infrastructure/mappers/types';

/**
 * Creates a repository for Reports API operations
 *
 * Implements IReportsApiPort by coordinating between the HTTP client and mappers.
 * Converts between application Dtos and API types.
 *
 * @param props - Configuration object
 * @param props.httpClient - HTTP client instance for making API requests
 * @param props.mapper - Mapper instance for converting between Dtos and API types
 * @returns ReportsApiRepository instance implementing IReportsApiPort
 *
 * @example
 * const apiRepository = createReportsApiRepository({
 *   httpClient: reportsHttpClient,
 *   mapper: reportApiMapper,
 * });
 */
export const createReportsApiRepository = (props: {
  httpClient: ReportsHttpClientType;
  mapper: ReportApiMapperType;
}): IReportsApiPort => {
  const { httpClient, mapper } = props;

  /**
   * Fetches available filter options for reports
   *
   * @param filterConfigs - Filter configurations from report definition
   * @returns Promise with filter options as Value Objects
   */
  const fetchFilterCatalog = async (
    filterConfigs: ReadonlyArray<ReportFilterConfig>,
  ): Promise<ReadonlyArray<ReportFilterOptions>> => {
    const apiRequest = mapper.mapFilterConfigToRequestApiDto(filterConfigs);

    const apiResponse = await httpClient.fetchFilterCatalog(apiRequest);

    const filterOptions = mapper.mapFilterResponseApiDtoToFilterOptions(apiResponse);

    return filterOptions;
  };

  /**
   * Fetches table block data based on filters
   *
   * @param request - Block data request with filters (Dto)
   * @param endpoint - Specific endpoint for the table block
   * @returns Promise with table data (Dto)
   */
  const fetchTableBlock = async (request: BlockDataRequestDto, endpoint: string): Promise<TableBlockData> => {
    const apiRequest = mapper.mapBlockDataRequestDtoToApiRequest(request);

    const apiResponse = await httpClient.fetchTableBlock(apiRequest, endpoint);

    const tableBlockResponse = mapper.mapApiTableBlockResponseToTableBlockData(apiResponse);

    return tableBlockResponse;
  };

  /**
   * Fetches chart block data based on filters
   *
   * @param request - Block data request with filters (Dto)
   * @param endpoint - Specific endpoint for the chart block
   * @returns Promise with chart data (Dto)
   */
  const fetchChartBlock = async (request: BlockDataRequestDto, endpoint: string): Promise<ChartBlockData> => {
    const apiRequest = mapper.mapBlockDataRequestDtoToApiRequest(request);

    const apiResponse = await httpClient.fetchChartBlock(apiRequest, endpoint);

    const chartBlockResponse = mapper.mapApiChartBlockResponseToChartBlockData(apiResponse);
    return chartBlockResponse;
  };

  /**
   * Fetches summary card block data based on filters
   *
   * @param request - Block data request with filters (Dto)
   * @param endpoint - Specific endpoint for the summary card block
   * @returns Promise with summary card data (Dto)
   */
  const fetchSummaryCardBlock = async (
    request: BlockDataRequestDto,
    endpoint: string,
  ): Promise<SummaryCardBlockData> => {
    const apiRequest = mapper.mapBlockDataRequestDtoToApiRequest(request);

    const apiResponse = await httpClient.fetchSummaryCardBlock(apiRequest, endpoint);

    const summaryCardBlockResponse = mapper.mapApiSummaryCardBlockResponseToSummaryCardBlockData(apiResponse);
    return summaryCardBlockResponse;
  };

  return {
    fetchFilterCatalog,
    fetchTableBlock,
    fetchChartBlock,
    fetchSummaryCardBlock,
  };
};
